import { useEffect, useMemo } from 'react';
import { auth } from '../../firebase/config';
import type { DashboardData } from '../../services/api';
import '../../styles/greeting.css';

interface DashboardGreetingProps {
  firstName: string;
  data: DashboardData | null;
}

// The dashboard call (which carries the real first name) is unconditional on every page
// load — it also drives revenue/visits/payments, so there's no fetch to skip. What we can
// skip is the awkward guess-from-email fallback while it's in flight: remember the last
// first name the backend gave us per account, and use that as the initial render instead.
const cacheKeyFor = (uid: string) => `greeting_first_name:${uid}`;

const getCachedFirstName = (uid: string): string => {
  try {
    return localStorage.getItem(cacheKeyFor(uid)) ?? '';
  } catch {
    return '';
  }
};

const setCachedFirstName = (uid: string, name: string) => {
  try {
    localStorage.setItem(cacheKeyFor(uid), name);
  } catch {
    // Best-effort — private browsing / storage disabled just means no cache next time.
  }
};

// Time bucket → Spanish greeting + a material-symbols icon (no emojis).
const timeBucket = (hour: number): { greeting: string; icon: string } => {
  if (hour >= 5 && hour < 12) return { greeting: 'Buenos días', icon: 'wb_sunny' };
  if (hour >= 12 && hour < 19) return { greeting: 'Buenas tardes', icon: 'wb_twilight' };
  return { greeting: 'Buenas noches', icon: 'dark_mode' };
};

// Prettify an email local-part as a last-resort name, e.g. "josue.perez" → "Josue".
const nameFromEmail = (email?: string | null): string => {
  const local = email?.split('@')[0]?.split(/[.\-_0-9]/)[0];
  if (!local) return '';
  return local.charAt(0).toUpperCase() + local.slice(1);
};

const resolveFirstName = (firstName: string): string => {
  const fromApi = firstName?.trim();
  if (fromApi) return fromApi.split(/\s+/)[0];
  const uid = auth.currentUser?.uid;
  const cached = uid ? getCachedFirstName(uid) : '';
  if (cached) return cached;
  const display = auth.currentUser?.displayName?.trim();
  if (display) return display.split(/\s+/)[0];
  return nameFromEmail(auth.currentUser?.email);
};

// Dynamic subline derived from today's real numbers — first match wins.
const buildSubline = (data: DashboardData | null): string => {
  if (!data) return '';
  const { visits_today, pending_payments, patients } = data;
  if (visits_today > 0) {
    return `Tienes ${visits_today} ${visits_today === 1 ? 'visita' : 'visitas'} hoy.`;
  }
  if (pending_payments.count > 0) {
    return `${pending_payments.count} ${pending_payments.count === 1 ? 'pago pendiente' : 'pagos pendientes'} por revisar.`;
  }
  if (patients.new_this_month > 0) {
    return `${patients.new_this_month} ${patients.new_this_month === 1 ? 'paciente nuevo' : 'pacientes nuevos'} este mes.`;
  }
  return 'Todo en orden por aquí.';
};

export const DashboardGreeting = ({ firstName, data }: DashboardGreetingProps) => {
  const { greeting, icon } = useMemo(() => timeBucket(new Date().getHours()), []);
  const name = useMemo(() => resolveFirstName(firstName), [firstName]);
  const subline = buildSubline(data);

  // Once the dashboard call actually confirms the first name, refresh the cache so next
  // load's initial render is right instead of falling back to the email-derived guess.
  useEffect(() => {
    const trimmed = firstName?.trim();
    const uid = auth.currentUser?.uid;
    if (uid && trimmed) {
      setCachedFirstName(uid, trimmed.split(/\s+/)[0]);
    }
  }, [firstName]);

  return (
    <div className="mb-8 flex flex-col items-center text-center">
      <h1 className="greeting-rise flex items-center justify-center gap-2.5 text-2xl sm:text-3xl font-semibold text-[#101822]">
        <span
          className="greeting-icon material-symbols-outlined text-primary text-3xl sm:text-4xl"
          style={{ fontVariationSettings: '"FILL" 1, "wght" 600, "GRAD" 0, "opsz" 40' }}
          aria-hidden="true"
        >
          {icon}
        </span>
        <span>
          {greeting}
          {name && (
            <>
              , <span className="greeting-name">{name}</span>
            </>
          )}
        </span>
      </h1>

      {subline && (
        <p className="greeting-rise greeting-delay-2 mt-2 text-base text-slate-500">{subline}</p>
      )}
    </div>
  );
};
